import type { Question, SurveyResponse } from '../types';

export class AnswerPreview {
  static renderAnswerPreview(surveyResponse: SurveyResponse, questions: Question[]): string {
    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV;
    
    if (!isDevelopment) {
      return '';
    }

    const answersHtml = this.renderAnswersList(surveyResponse.answers, questions);
    const jsonHtml = this.renderJsonPreview(surveyResponse);

    return `
      <div class="answer-preview-container" style="
        background: #f8f9fa; 
        border: 2px solid #007bff; 
        border-radius: 8px; 
        padding: 20px; 
        margin: 20px 0;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      ">
        <div style="
          background: #007bff; 
          color: white; 
          padding: 8px 12px; 
          margin: -20px -20px 15px -20px; 
          border-radius: 6px 6px 0 0; 
          font-weight: bold;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <span style="font-size: 16px;">🧪</span>
          DEV MODE: Answer Preview
        </div>
        
        <div class="answer-preview-tabs" style="margin-bottom: 15px;">
          <button 
            class="preview-tab-btn active" 
            data-tab="formatted"
            style="
              background: #007bff; 
              color: white; 
              border: none; 
              padding: 8px 16px; 
              border-radius: 4px 4px 0 0; 
              cursor: pointer;
              font-size: 12px;
              font-weight: 500;
              margin-right: 2px;
            "
          >
            Formatted Answers
          </button>
          <button 
            class="preview-tab-btn" 
            data-tab="json"
            style="
              background: #6c757d; 
              color: white; 
              border: none; 
              padding: 8px 16px; 
              border-radius: 4px 4px 0 0; 
              cursor: pointer;
              font-size: 12px;
              font-weight: 500;
            "
          >
            JSON for API
          </button>
        </div>

        <div class="preview-tab-content" id="formatted-tab" style="display: block;">
          ${answersHtml}
        </div>

        <div class="preview-tab-content" id="json-tab" style="display: none;">
          ${jsonHtml}
        </div>
      </div>
    `;
  }

  private static renderAnswersList(answers: Record<string, string | string[] | null>, questions: Question[]): string {
    const answerEntries = Object.entries(answers);
    
    if (answerEntries.length === 0) {
      return '<p style="color: #6c757d; font-style: italic;">No answers provided yet.</p>';
    }

    return `
      <div class="answers-list">
        ${answerEntries.map(([questionId, answer]) => {
          const question = questions.find(q => q.id === questionId);
          const questionText = question?.question || questionId;
          const questionType = question?.type || 'unknown';
          
          return `
            <div style="
              background: white; 
              border: 1px solid #dee2e6; 
              border-radius: 6px; 
              padding: 12px; 
              margin-bottom: 8px;
            ">
              <div style="
                font-weight: 600;
                color: #495057;
                font-size: 13px;
                margin-bottom: 4px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              ">
                <span style="white-space: pre-wrap;">${questionText}</span>
                <span style="
                  background: #e9ecef;
                  color: #6c757d;
                  padding: 2px 6px;
                  border-radius: 3px;
                  font-size: 10px;
                  font-weight: 400;
                ">${questionType}</span>
              </div>
              <div style="color: #212529; font-size: 14px;">
                ${this.formatAnswer(answer)}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  private static renderJsonPreview(surveyResponse: SurveyResponse): string {
    const jsonString = JSON.stringify(surveyResponse, null, 2);
    
    return `
      <div style="position: relative;">
        <div style="
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 8px;
          font-size: 12px;
          color: #6c757d;
        ">
          <span>API Payload (Ready for submission)</span>
          <button 
            onclick="navigator.clipboard.writeText(this.getAttribute('data-json'))" 
            data-json='${jsonString.replace(/'/g, "&#39;")}'
            style="
              background: #28a745; 
              color: white; 
              border: none; 
              padding: 4px 8px; 
              border-radius: 3px; 
              cursor: pointer; 
              font-size: 11px;
            "
            title="Copy JSON to clipboard"
          >
            📋 Copy JSON
          </button>
        </div>
        <pre style="
          background: #2d3748; 
          color: #e2e8f0; 
          padding: 15px; 
          border-radius: 6px; 
          overflow-x: auto; 
          font-family: 'Fira Code', 'Consolas', 'Monaco', monospace; 
          font-size: 12px; 
          line-height: 1.4;
          margin: 0;
        "><code>${this.highlightJson(jsonString)}</code></pre>
      </div>
    `;
  }

  private static formatAnswer(answer: string | string[] | null): string {
    if (answer === null || answer === undefined) {
      return '<span style="color: #6c757d; font-style: italic;">No answer</span>';
    }

    if (Array.isArray(answer)) {
      if (answer.length === 0) {
        return '<span style="color: #6c757d; font-style: italic;">No selections</span>';
      }
      return `
        <ul style="margin: 0; padding-left: 18px;">
          ${answer.map(item => `<li style="white-space: pre-wrap;">${item}</li>`).join('')}
        </ul>
      `;
    }

    if (answer === '') {
      return '<span style="color: #6c757d; font-style: italic;">Empty</span>';
    }

    return `<span style="white-space: pre-wrap;">${answer.toString()}</span>`;
  }

  private static highlightJson(jsonString: string): string {
    return jsonString
      .replace(/("[\w]+"):/g, '<span style="color: #81c784;">$1</span>:')
      .replace(/:\s*(".*?")/g, ': <span style="color: #ffb74d;">$1</span>')
      .replace(/:\s*(true|false)/g, ': <span style="color: #f48fb1;">$1</span>')
      .replace(/:\s*(\d+)/g, ': <span style="color: #64b5f6;">$1</span>')
      .replace(/:\s*(null)/g, ': <span style="color: #90a4ae;">$1</span>');
  }

  static attachTabListeners(): void {
    const tabs = document.querySelectorAll('.preview-tab-btn') as NodeListOf<HTMLElement>;
    const contents = document.querySelectorAll('.preview-tab-content') as NodeListOf<HTMLElement>;
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.getAttribute('data-tab');
        
        // Update tab buttons
        tabs.forEach(t => {
          if (t === tab) {
            t.style.background = '#007bff';
            t.classList.add('active');
          } else {
            t.style.background = '#6c757d';
            t.classList.remove('active');
          }
        });
        
        // Update tab contents
        contents.forEach(content => {
          if (content.id === tabName + '-tab') {
            content.style.display = 'block';
          } else {
            content.style.display = 'none';
          }
        });
      });
    });

    // Handle copy button functionality
    const copyButtons = document.querySelectorAll('.answer-preview-container button[onclick]') as NodeListOf<HTMLElement>;
    copyButtons.forEach(button => {
      button.removeAttribute('onclick');
      button.addEventListener('click', () => {
        const jsonText = button.getAttribute('data-json');
        if (jsonText) {
          navigator.clipboard.writeText(jsonText).then(() => {
            const originalText = button.textContent;
            button.textContent = '✓ Copied!';
            button.style.background = '#007bff';
            setTimeout(() => {
              button.textContent = originalText;
              button.style.background = '#28a745';
            }, 2000);
          });
        }
      });
    });
  }
}
